'use client';

import { useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import { ARABIC_ALPHABET, BASICS, LETTER_NAME_AR } from '@/lib/alphabet';
import { getArabic, getTransliteration, getTranslation } from '@/lib/quran-data';
import { speakLetter as speakLetterQueue, speakRepeat } from '@/lib/speech';
import { Eye, EyeOff, Volume2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function LearnSection() {
  const { uiLang, surah, translationEd } = useApp();
  const t = DICTS[uiLang];
  const [selected, setSelected] = useState(1); // letter index
  const [memorize, setMemorize] = useState(false);
  const letter = ARABIC_ALPHABET[selected];

  // Carte parlante : nom phonétique → lettre → lettre (moteur vocal hybride)
  const speakLetter = () => speakLetterQueue(LETTER_NAME_AR[letter.char] || letter.name, letter.char);

  return (
    <>
      <PageBanner
        src="/images/banners/learn_banner.png"
        eyebrow="Alphabet — الأبجدية"
        title={t.learn.title}
        sub={t.learn.subtitle}
      />
      <section id="section-learn" className="mx-auto max-w-5xl scroll-mt-20 px-4 py-14">

      {/* Alphabet grid */}
      <h3 className="mb-4 text-center text-lg font-semibold tracking-wide text-[#f0d98c]">{t.learn.alphabet_title}</h3>
      <div className="mb-6 grid grid-cols-4 gap-2 sm:grid-cols-7 md:grid-cols-14">
        {ARABIC_ALPHABET.map((l, i) => (
          <button
            key={l.name + i}
            onClick={() => {
              setSelected(i);
              speakLetterQueue(LETTER_NAME_AR[ARABIC_ALPHABET[i].char] || ARABIC_ALPHABET[i].name, ARABIC_ALPHABET[i].char);
            }}
            className={cn(
              'font-arabic flex aspect-square items-center justify-center rounded-lg border text-2xl transition-all',
              selected === i
                ? 'border-[#d4af37] bg-[#d4af37] text-[#032019] shadow-[0_0_18px_rgba(212,175,55,0.45)]'
                : 'border-[rgba(212,175,55,0.3)] bg-[rgba(7,53,40,0.6)] text-[#f5f0e1] hover:border-[rgba(212,175,55,0.7)] hover:bg-[rgba(212,175,55,0.12)]'
            )}
            aria-label={l.name}
          >
            {l.char}
          </button>
        ))}
      </div>

      {/* Letter detail */}
      <div className="gold-ornate-card mb-10 rounded-2xl p-6">
        <div className="mb-5 flex flex-col items-center gap-4 sm:flex-row">
          <div className="font-arabic flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl border-2 border-[rgba(212,175,55,0.5)] bg-gradient-to-br from-[rgba(212,175,55,0.15)] to-transparent text-6xl text-[#f0d98c]">
            {letter.char}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p className="font-serif-royal text-2xl font-bold text-[#f0d98c]">{letter.name}</p>
            <p className="mt-1 text-sm text-[#cfe0cf]/85">{uiLang === 'ar' ? letter.soundEn : uiLang === 'en' ? letter.soundEn : letter.soundFr}</p>
            <button
              onClick={speakLetter}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-[rgba(212,175,55,0.4)] px-3 py-1.5 text-xs font-semibold text-[#f0d98c] hover:bg-[rgba(212,175,55,0.12)]"
            >
              <Volume2 className="h-3.5 w-3.5" /> Écouter
            </button>
          </div>
        </div>

        {/* Forms */}
        <p className="mb-1 text-center text-xs font-semibold tracking-wider text-[#d4af37] uppercase">{t.learn.forms_title}</p>
        <p className="mb-4 text-center text-xs text-[#cfe0cf]/60">{t.learn.forms_note}</p>
        <div className="grid grid-cols-4 gap-2">
          {(['isolated', 'initial', 'medial', 'final'] as const).map((f) => (
            <div key={f} className="rounded-lg border border-[rgba(212,175,55,0.25)] bg-[rgba(3,32,25,0.6)] py-3 text-center">
              <div className="font-arabic text-3xl text-[#f5f0e1]">{letter.forms[f]}</div>
              <div className="mt-1 text-[10px] tracking-wide text-[#cfe0cf]/60 uppercase">{t.learn.forms[f]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Basics formulas */}
      <h3 className="mb-4 text-center text-lg font-semibold tracking-wide text-[#f0d98c]">{t.learn.basics_title}</h3>
      <div className="mb-10 grid gap-3 sm:grid-cols-2">
        {BASICS.map((b) => {
          const info = t.learn.basics[b.key as keyof typeof t.learn.basics];
          return (
            <div key={b.key} className="gold-ornate-card rounded-xl p-5 text-center">
              <p className="font-arabic mb-2 text-2xl leading-relaxed text-[#f5f0e1]" dir="rtl" lang="ar">{b.arabic}</p>
              <p className="mb-1 text-xs italic text-[#e8c86a]/90">{b.translit}</p>
              <p className="font-serif-royal text-base font-bold text-[#f0d98c]">{info.t}</p>
              <p className="mt-1 text-xs leading-relaxed text-[#cfe0cf]/75">{info.d}</p>
            </div>
          );
        })}
      </div>

      {/* Memorization mode */}
      <div className="gold-ornate-card rounded-2xl p-6">
        <div className="mb-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
          <div className="text-center sm:text-left">
            <h3 className="font-serif-royal text-xl font-bold text-[#f0d98c]">{t.learn.memorize_title}</h3>
            <p className="text-xs text-[#cfe0cf]/70">{t.learn.memorize_desc}</p>
          </div>
          <button
            onClick={() => setMemorize(!memorize)}
            className={cn(
              'flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-bold transition-colors',
              memorize
                ? 'bg-[#d4af37] text-[#032019]'
                : 'border border-[rgba(212,175,55,0.5)] text-[#f0d98c] hover:bg-[rgba(212,175,55,0.12)]'
            )}
          >
            {memorize ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {memorize ? t.learn.hide_all : t.learn.show_all}
          </button>
        </div>
        <div className="space-y-3">
          <MemorizeList memorize={memorize} surah={surah} edition={translationEd} />
        </div>
      </div>
      </section>
    </>
  );
}

function MemorizeList({ memorize, surah, edition }: { memorize: boolean; surah: number; edition: string }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});
  const arabic = getArabic(surah);
  const translit = getTransliteration(surah);
  const translation = getTranslation(surah, edition);

  return (
    <>
      {arabic.map((a, idx) => (
        <div key={a.n} className="rounded-lg border border-[rgba(212,175,55,0.2)] bg-[rgba(3,32,25,0.5)] p-4">
          <p className="font-arabic mb-2 text-right text-xl text-[#f5f0e1]" dir="rtl" lang="ar">{a.text}</p>
          <div className="text-sm">
            {memorize && !revealed[a.n] ? (
              <button
                onClick={() => setRevealed({ ...revealed, [a.n]: true })}
                className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-[rgba(212,175,55,0.4)] py-2 text-xs font-semibold text-[#d4af37]/80 hover:bg-[rgba(212,175,55,0.08)]"
              >
                <Eye className="h-3.5 w-3.5" /> {idx + 1} — Révéler
              </button>
            ) : (
              <>
                <p className="italic text-[#e8c86a]/80">{translit[idx]?.text}</p>
                <p className="mt-1 text-[#cfe0cf]/90">{translation[idx]?.text}</p>
              </>
            )}
          </div>
        </div>
      ))}
    </>
  );
}
